import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import * as partitionTypesApi from '@/api/modules/partitionTypes';
import { normalizeData, normalizeIncluded } from '@/utils/jsonapi';
import { TENANT_SWITCH_FULFILLED } from '@features/auth/tenantSwitchActionType';

/**
 * Entity adapter for normalized partition type state
 */
const partitionTypesAdapter = createEntityAdapter({
  selectId: (partitionType) => partitionType.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Async thunk to fetch all partition types
 */
export const fetchPartitionTypesThunk = createAsyncThunk(
  'partitionTypes/fetchAll',
  async (queryOptions = {}, { rejectWithValue }) => {
    try {
      const response = await partitionTypesApi.fetchPartitionTypes(queryOptions);

      // Response is JSON:API format: { data: [...], meta: {...}, included: [...] }
      const normalized = normalizeData(response.data);

      // Process included resources (e.g., partition_values if included)
      const included = normalizeIncluded(response.included || []);

      return {
        data: normalized || [],
        meta: response.meta,
        included: response.included
      };
    } catch (error) {
      // Extract error message from JSON:API format
      let errorMessage = 'Failed to fetch partition types';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to fetch a single partition type by ID
 */
export const fetchPartitionTypeDetailThunk = createAsyncThunk(
  'partitionTypes/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionTypesApi.fetchPartitionTypeDetail(id);

      // Process included resources
      const included = normalizeIncluded(jsonApiResponse.included || []);

      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      let errorMessage = 'Failed to fetch partition type';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to create a new partition type
 */
export const createPartitionTypeThunk = createAsyncThunk(
  'partitionTypes/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionTypesApi.createPartitionType(data);
      const included = normalizeIncluded(jsonApiResponse.included || []);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      let errorMessage = 'Failed to create partition type';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to create a new partition type inline (for wizard workflows)
 * Includes optimistic update for instant UI feedback
 * Returns normalized partition type object for immediate use in wizard
 */
export const createPartitionTypeInlineThunk = createAsyncThunk(
  'partitionTypes/createInline',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionTypesApi.createPartitionType(data);
      const included = normalizeIncluded(jsonApiResponse.included || []);
      const normalized = normalizeData(jsonApiResponse.data);

      // Return with metadata for inline creation context
      return {
        ...normalized,
        _isInlineCreation: true,
        _createdAt: new Date().toISOString()
      };
    } catch (error) {
      // Enhanced error parsing for wizard validation
      let errorMessage = 'Failed to create partition type';

      // Check for duplicate name error
      if (error.response?.data?.errors) {
        const nameError = error.response.data.errors.find(e =>
          e.source?.pointer === '/data/attributes/name' || e.detail?.includes('name')
        );
        if (nameError) {
          errorMessage = 'Partition type name already exists';
        }
      } else if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to update an existing partition type
 */
export const updatePartitionTypeThunk = createAsyncThunk(
  'partitionTypes/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionTypesApi.updatePartitionType(id, data);
      const included = normalizeIncluded(jsonApiResponse.included || []);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      let errorMessage = 'Failed to update partition type';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to delete a partition type
 */
export const deletePartitionTypeThunk = createAsyncThunk(
  'partitionTypes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await partitionTypesApi.deletePartitionType(id);
      return id;
    } catch (error) {
      let errorMessage = 'Failed to delete partition type';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Partition Types slice with entity adapter
 */
const partitionTypesSlice = createSlice({
  name: 'partitionTypes',
  initialState: partitionTypesAdapter.getInitialState({
    currentPartitionType: null,
    loading: false,
    error: null,
    createInlineLoading: false,
    createInlineError: null,
    pagination: null,
    included: null,
  }),
  reducers: {
    /**
     * Clear inline creation error (for dismissing wizard errors)
     */
    clearInlineError: (state) => {
      state.createInlineError = null;
    },
    addOne: partitionTypesAdapter.addOne,
    addMany: partitionTypesAdapter.addMany,
    updateOne: partitionTypesAdapter.updateOne,
    updateMany: partitionTypesAdapter.updateMany,
    upsertOne: partitionTypesAdapter.upsertOne,
    upsertMany: partitionTypesAdapter.upsertMany,
    removeOne: partitionTypesAdapter.removeOne,
    removeMany: partitionTypesAdapter.removeMany,
    setAll: partitionTypesAdapter.setAll,

    setCurrentPartitionType: (state, action) => {
      state.currentPartitionType = action.payload;
    },

    clearAll: (state) => {
      partitionTypesAdapter.removeAll(state);
      state.currentPartitionType = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Partition Types
      .addCase(fetchPartitionTypesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartitionTypesThunk.fulfilled, (state, action) => {
        state.loading = false;
        let entities = action.payload?.data || [];

        // Resolve included partition_values into their parent partition types
        const included = action.payload?.included || [];
        if (included.length > 0) {
          const includedByType = normalizeIncluded(included);
          const pvMap = includedByType['partition_value'] || {};
          entities = entities.map((pt) => {
            if (Array.isArray(pt.partition_values) && pt.partition_values.length > 0) {
              const resolved = pt.partition_values
                .map((ref) => pvMap[ref.id || ref] || ref)
                .filter((pv) => pv.value !== undefined || pv.display_name !== undefined);
              if (resolved.length > 0) {
                return { ...pt, partition_values: resolved };
              }
            }
            return pt;
          });
        }

        partitionTypesAdapter.setAll(state, entities);
        state.pagination = action.payload?.meta?.pagination || null;
        state.included = action.payload?.included || null;
      })
      .addCase(fetchPartitionTypesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Detail
      .addCase(fetchPartitionTypeDetailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartitionTypeDetailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPartitionType = action.payload;
        partitionTypesAdapter.upsertOne(state, action.payload);
      })
      .addCase(fetchPartitionTypeDetailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createPartitionTypeThunk.fulfilled, (state, action) => {
        partitionTypesAdapter.addOne(state, action.payload);
      })
      // Create Inline
      .addCase(createPartitionTypeInlineThunk.pending, (state) => {
        state.createInlineLoading = true;
        state.createInlineError = null;
      })
      .addCase(createPartitionTypeInlineThunk.fulfilled, (state, action) => {
        state.createInlineLoading = false;
        partitionTypesAdapter.addOne(state, action.payload);
      })
      .addCase(createPartitionTypeInlineThunk.rejected, (state, action) => {
        state.createInlineLoading = false;
        state.createInlineError = action.payload;
      })
      // Update
      .addCase(updatePartitionTypeThunk.fulfilled, (state, action) => {
        partitionTypesAdapter.upsertOne(state, action.payload);
        if (state.currentPartitionType?.id === action.payload.id) {
          state.currentPartitionType = action.payload;
        }
      })
      // Delete
      .addCase(deletePartitionTypeThunk.fulfilled, (state, action) => {
        partitionTypesAdapter.removeOne(state, action.payload);
        if (state.currentPartitionType?.id === action.payload) {
          state.currentPartitionType = null;
        }
      })
      // Clear partition types when tenant switches
      .addCase(TENANT_SWITCH_FULFILLED, (state) => {
        partitionTypesAdapter.removeAll(state);
        state.currentPartitionType = null;
        state.error = null;
        state.loading = false;
      });
  },
});

// Export actions
export const {
  clearInlineError,
  addOne,
  addMany,
  updateOne,
  updateMany,
  upsertOne,
  upsertMany,
  removeOne,
  removeMany,
  setAll,
  setCurrentPartitionType,
  clearAll,
} = partitionTypesSlice.actions;

// Export entity adapter selectors with safe defaults
export const {
  selectAll: selectAllPartitionTypes,
  selectById: selectPartitionTypeById,
  selectIds: selectPartitionTypeIds,
  selectEntities: selectPartitionTypeEntities,
  selectTotal: selectPartitionTypesTotal,
} = partitionTypesAdapter.getSelectors((state) => state.partitionTypes || partitionTypesAdapter.getInitialState());

// Custom selectors
export const selectPartitionTypesLoading = (state) => state.partitionTypes?.loading || false;
export const selectPartitionTypesError = (state) => state.partitionTypes?.error || null;
export const selectCurrentPartitionType = (state) => state.partitionTypes?.currentPartitionType || null;
export const selectCreateInlineLoading = (state) => state.partitionTypes?.createInlineLoading || false;
export const selectCreateInlineError = (state) => state.partitionTypes?.createInlineError || null;

/**
 * Select active partition types only
 */
export const selectActivePartitionTypes = createSelector(
  [selectAllPartitionTypes],
  (partitionTypes) => partitionTypes.filter(pt => pt.is_active !== false)
);

/**
 * Select partition types formatted for selection in wizard
 * Returns: [{ id, name, description, valueCount }]
 */
export const selectPartitionTypesForSelection = createSelector(
  [selectActivePartitionTypes],
  (partitionTypes) => partitionTypes.map(pt => ({
    id: pt.id,
    name: pt.name,
    description: pt.description || '',
    valueCount: pt.partition_values?.length || pt.value_count || 0,
  })).sort((a, b) => a.name.localeCompare(b.name))
);

/**
 * Select available partition types for metric creation wizard
 * Active partition types sorted alphabetically with value counts
 */
export const selectAvailablePartitionTypes = createSelector(
  [selectActivePartitionTypes],
  (partitionTypes) => partitionTypes.sort((a, b) => a.name.localeCompare(b.name))
);

// Legacy selectors for backward compatibility
export const selectPartitionTypes = selectAllPartitionTypes;

// Export reducer
export default partitionTypesSlice.reducer;
